import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { AppRoutes } from "_config/routes";
import { useAppSettings, useLocalStorage, useMediaHelp, useQuota, useScreen, useTabSync } from "_hooks";
import { NubeskLoader } from "components/molecules"
import { Modal } from "components/templates";
import { isLoggedIn } from "_redux/user/user.actions";
import { selectAppConfigGot } from "_redux/app/app.selector";
import { selectUser, selectUserStatus } from "_redux/user/user.selectors";
import { selectModal } from "_redux/ui/ui.selector";
import { closeLoader } from "_redux/ui/ui.actions";
import { getCookie, setCookie } from "_utils/cookie";
import { LOADING, IDLE, FAILURE, SUCCESS } from "_constants/redux.constants";

let timer;
let checkTimer;
let second = 1000;
let minute = 60 * second;
let hour = 60 * minute;
let duration = parseInt(process.env.REACT_APP_SESSION_EXPIRES || 8 * hour);
let cookieName = "Nubesk_v2.0_session";

const Routes = () => {
  const dispatch = useDispatch();

  const initialized = useRef(null);
  const appMounted = useRef(false);

  const userStatus = useSelector(selectUserStatus);
  const user = useSelector(selectUser);
  const appConfigGot = useSelector(selectAppConfigGot);
  const modal = useSelector(selectModal);

  const workspaceChanged = modal?.isOpen && modal?.typeModal === "workspaceChanged";

  const { fetchAllSettings } = useAppSettings();
  const [getLS, setLs] = useLocalStorage();
  const { launchVideo } = useMediaHelp();
  const { getSizes } = useScreen();
  const { isOkRemoval, showExceededRemoval } = useQuota();

  useTabSync();

  useEffect(() => {
    if (!initialized?.current) {
      initialized.current = true;

      // Workspace switch detection: auth redirects here with ?ws=<workspaceId>.
      // localStorage is origin-isolated, so the cache cleanup must happen in the
      // app's own origin. If the incoming workspace differs from the cached one,
      // wipe the cache so every workspace-scoped value is refetched from the
      // backend (fetchAllSettings, triggered once `user` arrives, reads from an
      // empty cache and hits the API).
      const params = new URLSearchParams(window.location.search);
      const incomingWs = params.get("ws");
      if (incomingWs) {
        const storedWs = getLS("workspaceId");
        if (incomingWs !== storedWs) {
          localStorage.clear();
          setLs("workspaceId", incomingWs);
        }
        params.delete("ws");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
        );
      }

      let clientLs = getLS("client");
      if (!clientLs?.screen?.validate) {
        let screen = getSizes();
        
        setLs("client", {
          ...clientLs ?? {},
          screen: {
            validate: true,
            ...screen
          }
        });
        if (screen?.width < 1024) {
          window.location = "/screenNotCompatible";
        }
      }

      if (userStatus === IDLE) {
        // Wait for the isLoggedIn() response and check the actual session data
        // before redirecting: only send the user to login when the backend
        // confirms there is no valid session (no user in the payload).
        dispatch(isLoggedIn()).then((result) => {
          const stillValid = !!result?.data?.[0];
          if (!stillValid) {
            window.location.replace(`${process.env.REACT_APP_CORE_WEB}/account/login`);
          }
        });
      }
    }
    
    return () => {
      if (checkTimer) clearTimeout(checkTimer);
    }
  }, []);

  useEffect(() => {
    if (userStatus === FAILURE) {
      timer = setTimeout(() => dispatch(closeLoader()), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    }
  }, [userStatus]);


  useEffect(() => {
    if (!!user?.__freeTrial) {
      let expired = false;
      let { startDate, ttl } = user?.__freeTrial;
      let now = moment();
      let end = moment(startDate).add(ttl, "days");

      if (end < now) expired = true;

      if (!expired && user?.__analytics?.backremovals) {
        expired = !(isOkRemoval());
      }

      if (expired) {
        window.location.replace(process.env.REACT_APP_EXPIRED_TRIAL);
        return;
      }
    } else if (!isOkRemoval()) {
      showExceededRemoval();
    }
    
    if (user) fetchAllSettings();
    
  }, [user]);

  useEffect(() => {
    if (!user || !appConfigGot) return; 
    launchVideo();
  }, [user, appConfigGot]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && userStatus === SUCCESS) {
      let now = Date.now();
      let expires = getCookie(cookieName);
      let rest = expires - now;
      if (!(expires && now < expires)) {
        setCookie(cookieName, Date.now() + duration, duration);
      }

      checkTimer = setTimeout(() => {
        dispatch(isLoggedIn()).then((result) => {
          const stillValid = !!result?.data?.[0];
          if (!stillValid) {
            window.location.replace(`${process.env.REACT_APP_CORE_WEB}/account/login`);
          }
        });
      }, rest);
    }
  }, [userStatus]);

  // A workspace was changed in another tab: unmount the whole app so no
  // background requests keep running with the previous workspace's data, and
  // render the modal standalone (it lives inside AppRoutes/UserLayout otherwise).
  if (workspaceChanged) {
    return <Modal />;
  }

  const fullyReady = ![LOADING, IDLE].includes(userStatus) && user !== null && appConfigGot;
  if (fullyReady) appMounted.current = true;

  // Keep the app mounted during a transient re-verification (e.g. useTabSync
  // dispatches isLoggedIn() after another tab's clear, which briefly sets
  // userStatus=LOADING and user=null). Only fall back to the loader on the very
  // first load, before the app has ever been shown. A real invalid session ends
  // in FAILURE, which redirects to login (useEffect above / useTabSync).
  const showApp = fullyReady || (appMounted.current && userStatus !== FAILURE);

  return (
    <>
      <NubeskLoader visible={!showApp} />
      {showApp ? <AppRoutes /> : null}
    </>
  );
};

export default Routes;
